import { Destroyer } from '@adaptiva/core/common';
import { LoggingService } from '@adaptiva/core/logging';
import { Thread, ThreadingService } from '@adaptiva/core/threading';
import { inject, Injectable, OnDestroy } from '@angular/core';
import * as marked from 'marked';
import { BehaviorSubject, catchError, finalize, Observable, of, switchMap, timeout } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { IMarkdownParseResult } from '../interfaces';

/**
 * @author Jesse Rogers <jesse.rogers@adaptiva.com>
 * @description Angular Service to delegate markdown parsing to a managed async worker thread
 * @see markdown-processor.worker.ts
 */
@Injectable({
	providedIn: 'root'
})
export class MarkdownService extends Destroyer implements OnDestroy {

	public connectionTimeout: int = 3000;

	protected readonly threadName: string = 'markdown';

	protected readonly threading: ThreadingService = inject(ThreadingService);

	protected readonly logging: LoggingService = inject(LoggingService);

	public parse(_markdown: string): Observable<string> {
		const _parseId: string = uuid();

		// web workers are not supported, so return the string as is
		if (!this.threading.isSupported)
			return of(_markdown);

		const _parseQuery: () => Observable<IMarkdownParseResult> = () => {
			return this.threading.connect<IMarkdownParseResult>(this.threadName, _parseId, _markdown);
		};

		return _parseQuery().pipe(
			timeout({ first: this.connectionTimeout, with: _parseQuery }),
			switchMap((_result: IMarkdownParseResult) => {
				if (_result?.error)
					throw _result.error;

				return of(_result?.html || _markdown);
			}),
			catchError((e: Error) => {
				this.logging.error('MarkdownService.parse', this.logging.extractErrorMessage(e))();

				return of(_markdown);
			}),
			timeout({ first: this.connectionTimeout * 2, with: () => new BehaviorSubject(_markdown) }),
			finalize(() => {
				this.threading.disconnect(this.threadName, _parseId, false);
			})
		);
	}

	public syncParse(_markdown: string): string {
		try {
			return marked.parse(_markdown, { async: false });
		} catch (e) {
			this.logging.error('MarkdownService.syncParse', this.logging.extractErrorMessage(e))();
		}
	}

	public allocateThread(): Thread<IMarkdownParseResult> {
		return this.threading.create(this.threadName);
	}

}
